import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPlus,
    faPenToSquare,
    faTrash,
    faBolt,
    faLocationDot,
    faClock,
    faCalendarDays,
	faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";

import toast from "react-hot-toast";
import LoadingSpinner from "../../components/LoadingSpinner";
import ModalBase from "../../components/ModalBase";
import ConfirmationModal from "../../components/ConfirmationModal";
import { useAuth } from "../../providers/AuthContext";

import { localTimeToUtcTimeString, utcTimeToLocalTimeString } from "../../helpers/DateTime";

import SiteSmartChargingWizard from "./SiteSmartChargingWizard";

import { getChargingSiteCapacity, updateChargingSiteCapacity, createChargingSiteCapacitySchedule, deleteChargingSiteCapacity } from "../../api/ChargingSites";
import { hasMixedOcppVersions } from "../point/ocppPointHelpers";

const SiteSmartCharging = ({ chargingSite }) => {
    const { t } = useTranslation();
    const { getAccessToken } = useAuth();

    const [loading, setLoading] = useState(false);
    const [capacity, setCapacity] = useState(null);

    const [isWizardOpen, setIsWizardOpen] = useState(false);
    const [initialSchedule, setInitialSchedule] = useState(null);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmDeleteCapacityOpen, setConfirmDeleteCapacityOpen] = useState(false);

    const [scheduleToRemove, setScheduleToRemove] = useState(null);

    const siteId = chargingSite?.chargingSitePublicId;

    const schedules = (() => {
        const list = capacity?.schedules ?? [];
        return [...list].sort((a, b) => {
            const av = a?.validFrom ? new Date(a.validFrom).getTime() : 0;
            const bv = b?.validFrom ? new Date(b.validFrom).getTime() : 0;
            return av - bv;
        });
    })();

    useEffect(() => {
        loadCapacity();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteId]);

    const loadCapacity = async () => {
        if (!siteId) return;
        setLoading(true);
        try {
            const token = await getAccessToken();
            const dto = await getChargingSiteCapacity(token, siteId);
             setCapacity(dto);
        } catch (err) {
            console.error(err);
            toast.error(err?.message);
        } finally {
            setLoading(false);
        }
    };

    const siteHasPointNotSupportingSmartCharing = () => {
		return chargingSite.chargingPoints.filter((cp) => cp.supportsSmartCharging === false).length > 0;
    };

    const siteHasMixedOcppVersions = hasMixedOcppVersions(chargingSite?.chargingPoints);
    const mixedVersionMessage = t("smartCharging.mixedOcpp");

    // Create schedule (daily/weekly) - wizard får starta från Type
    const createNewSchedule = () => {
        if (siteHasMixedOcppVersions) {
            toast.error(mixedVersionMessage);
            return;
        }

        setInitialSchedule({
            // bara för att prefill:a site max i wizard (om du visar den)
            siteMaxKw: capacity?.maxPowerW != null ? String(Number(capacity.maxPowerW) / 1000) : "",

            scheduleMaxKw: "",
            recurrencyKind: "Daily",
            startDate: "",
            endDate: "",
            recurrenceDays: [],
            timeOfDayStart: "",
            timeOfDayEnd: "",
            existingChargingSitePowerSchedulePublicId: null,
        });

        setIsWizardOpen(true);
    };

    const editSiteMax = () => {
        if (siteHasMixedOcppVersions) {
            toast.error(mixedVersionMessage);
            return;
        }

        setInitialSchedule({
            flow: "siteMax", 
            recurrencyKind: null,

            siteMaxKw: capacity?.maxPowerW != null ? String(Number(capacity.maxPowerW) / 1000) : "",
            scheduleMaxKw: "",

            startDate: "",
            endDate: "",
            recurrenceDays: [],
            timeOfDayStart: "",
            timeOfDayEnd: "",
            existingChargingSitePowerSchedulePublicId: null,
        });

        setIsWizardOpen(true);
    };

    const editSchedule = (scheduleDto) => {
        if (siteHasMixedOcppVersions) {
            toast.error(mixedVersionMessage);
            return;
        }

        setInitialSchedule(convertDtoToWizard(scheduleDto, capacity));
        setIsWizardOpen(true);
    };

    const handleWizardSubmit = async (wizardData) => {
        if (siteHasMixedOcppVersions) {
            toast.error(mixedVersionMessage);
            return;
        }

        try {
            const token = await getAccessToken();

            const isSiteMaxFlow =
                wizardData?.flow === "siteMax" ||
                (!wizardData?.flow && (wizardData?.recurrencyKind == null || wizardData?.recurrencyKind === ""));

            const capacityPublicId = capacity?.chargingSiteCapacityPublicId ?? null;

            // Vi vill alltid utgå från nuvarande schedules när vi sparar,
            // annars finns risk att de försvinner vid PUT/POST.
            const existingSchedules = Array.isArray(capacity?.schedules) ? capacity.schedules : [];
            const existingMaxPowerW = capacity?.maxPowerW ?? null;

            // ---------------- SITE MAX ----------------
            if (isSiteMaxFlow) {
                const siteMaxStr = String(wizardData?.siteMaxKw ?? "").trim();
                const maxPowerW = siteMaxStr === "" ? null : Number(siteMaxStr) * 1000;

                const body = {
                    maxPowerW,
                    schedules: existingSchedules, // ✅ skicka med befintliga schedules
                };

                if (capacityPublicId) {
                    await updateChargingSiteCapacity(body, token, capacityPublicId);
                } else {
                    await createChargingSiteCapacitySchedule(token, siteId, body);
                }

                toast.success(t("smartCharging.siteMaxUpdated"));
                setIsWizardOpen(false);
                setInitialSchedule(null);

                await loadCapacity();
                return;
            }

            // ---------------- SCHEDULE (create/edit) ----------------
            const schedulePayload = wizardToScheduleDto(wizardData);

            // Om capacity inte finns än: skapa den med schedule direkt
            if (!capacityPublicId) {
                const body = {
                    // Behåll site max om wizard råkar ha det ifyllt, annars null
                    maxPowerW:
                        String(wizardData?.siteMaxKw ?? "").trim() === ""
                            ? null
                            : Number(String(wizardData.siteMaxKw).trim()) * 1000,

                    schedules: [schedulePayload],
                };

                await createChargingSiteCapacitySchedule(token, siteId, body);

                toast.success(t("smartCharging.scheduleCreated"));
                setIsWizardOpen(false);
                setInitialSchedule(null);
                await loadCapacity();
                return;
            }

            // Capacity finns: PUT med merge schedules
            const merged = mergeSchedules(existingSchedules, schedulePayload);

            const body = {
                maxPowerW: existingMaxPowerW, // ✅ påverka INTE site max vid schedule-save
                schedules: merged,
            };

            await updateChargingSiteCapacity(body, token, capacityPublicId);

            toast.success(
                wizardData?.existingChargingSitePowerSchedulePublicId ? t("smartCharging.scheduleUpdated") : t("smartCharging.scheduleCreated")
            );

            setIsWizardOpen(false);
            setInitialSchedule(null);

            await loadCapacity();
        } catch (err) {
            console.error(err);
            toast.error(err?.message || t("toast.failedSave"));
        }
    };

    const deleteSchedule = async (schedulePublicId) => {
        try {
            const token = await getAccessToken();

            if (!capacity?.chargingSiteCapacityPublicId) {
                toast.error(t("smartCharging.missingCapacity"));
                return;
            }

            const remainingSchedules = (capacity.schedules ?? []).filter(
                (s) => s.chargingSitePowerSchedulePublicId !== schedulePublicId
            );

            const body = {
                maxPowerW: capacity.maxPowerW ?? null, // 🔒 påverka INTE site max
                schedules: remainingSchedules,
            };

            await updateChargingSiteCapacity(
                body,
                token,
                capacity.chargingSiteCapacityPublicId
            );

            toast.success(t("smartCharging.scheduleRemoved"));

            await loadCapacity();
        } catch (err) {
            console.error(err);
            toast.error(err?.message || t("smartCharging.failedDeleteSchedule"));
        }
    };

    const deleteCapacity = async () => {
        try {
            const token = await getAccessToken();

            const capacityPublicId = capacity?.chargingSiteCapacityPublicId;
            if (!capacityPublicId) {
                toast.error(t("smartCharging.missingCapacityId"));
                return;
            }

            await deleteChargingSiteCapacity(token, capacityPublicId);

            toast.success(t("smartCharging.capacityDeleted"));

            setConfirmDeleteCapacityOpen(false);
            setIsWizardOpen(false);
            setInitialSchedule(null);

            setCapacity(null);
            await loadCapacity();
        } catch (err) {
            console.error(err);
            toast.error(err?.message || t("smartCharging.failedDeleteCapacity"));
        }
    };


    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-20">
                <LoadingSpinner text={t("smartCharging.loadingSchedules")} />
            </div>
        );
    }

    return (
        <>
            <div className="p-10 pb-5">
                <h1 className="text-2xl font-bold mb-3 ">{t("smartCharging.site")}</h1>

                <div className="border rounded-lg p-4 bg-white shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-bold flex items-center gap-3 ">
                            <FontAwesomeIcon icon={faLocationDot} className="text-sky-500" />
                            {chargingSite?.name ?? t("entity.chargingSite")}
                            {siteHasPointNotSupportingSmartCharing() && (
                                <>
                                    <FontAwesomeIcon icon={faTriangleExclamation}  className="text-red-600" />
                                    <span className="text-xs text-red-600">{t("smartCharging.somePointsNoSupport")}</span>
                                </>
                            )}
                            {siteHasMixedOcppVersions && (
                                <>
                                    <FontAwesomeIcon icon={faTriangleExclamation} className="text-red-600" />
                                    <span className="text-xs text-red-600">{t("smartCharging.differentOcpp")}</span>
                                </>
                            )}
                        </h2>

                        <div className="flex items-center gap-3">
                            <button
                                className="btn btn-primary hover:opacity-90 disabled:opacity-40"
                                disabled={siteHasMixedOcppVersions}
                                title={siteHasMixedOcppVersions ? mixedVersionMessage : undefined}
                                onClick={createNewSchedule}
                            >
                                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                                {t("smartCharging.newSchedule")}
                            </button>

                            <button
                                className="btn hover:bg-red-50 disabled:opacity-40"
                                title={t("smartCharging.deleteAll")}
                                disabled={!capacity?.chargingSiteCapacityPublicId}
                                onClick={() => setConfirmDeleteCapacityOpen(true)}
                            >
                                <FontAwesomeIcon icon={faTrash} className="text-red-600" />
                            </button>

                        </div>

                    </div>

                    <div className="space-y-4">
                    {/* Site max (global, NOT a schedule) */}
                    <div className="border rounded-xl bg-mute-50 shadow-sm">
                        <div className="flex justify-between items-center p-3">
                            <div className="space-y-1">
                                <div className="font-semibold text-lg ">
									{t("smartCharging.siteMaxPower")}
                                </div>

                                <div className="text-sm text-mute-700 flex flex-wrap items-center gap-2">
                                    <FontAwesomeIcon icon={faBolt} className="text-yellow-500 text-sm" />
                                    <span className="font-semibold">
                                        {capacity?.maxPowerW != null ? `${Number(capacity.maxPowerW) / 1000} kW` : t("common.unlimited")}
                                    </span>

                                    <span className="text-mute-400"> </span>

                                    <FontAwesomeIcon icon={faCalendarDays} className="text-mute-500 text-sm" />
                                    <span>{t("smartCharging.usedWhenNoSchedule")}</span>

                                    <span className="text-mute-400"> </span>

  
                                </div>
                            </div>

                            <div className="flex items-center gap-4 text-mute-700">
                                <button
                                        className="text-primary-600 hover:text-primary-700 disabled:opacity-40 disabled:cursor-not-allowed"
                                    onClick={editSiteMax}
                                    disabled={siteHasMixedOcppVersions}
                                    title={siteHasMixedOcppVersions ? mixedVersionMessage : t("common.edit")}
                                >
                                    <FontAwesomeIcon icon={faPenToSquare} />
                                </button>
                            </div>
                        </div>
                    </div>

                    {schedules.length === 0 ? (
                        <div className="text-mute-500 italic">{t("smartCharging.noSchedules")}</div>
                    ) : (
                        <div className="space-y-4">
                            {schedules.map((s) => {
                                const id =
                                    s.chargingSitePowerSchedulePublicId ??
                                    `${String(s.validFrom)}-${String(s.validTo)}-${String(s.maxPowerW)}`;

                                return (
                                    <div key={id} className="border rounded-xl bg-mute-50 shadow-sm">
                                        <div className="flex justify-between items-center p-3">
                                            <div className="space-y-1">
                                                <div className="font-semibold text-lg ">
                                                    {formatDate(s.validFrom)} - {formatDate(s.validTo)}
                                                </div>

                                                <div className="text-sm text-mute-700 flex flex-wrap items-center gap-2">
                                                    <FontAwesomeIcon icon={faBolt} className="text-yellow-500 text-sm" />
                                                    <span className="font-semibold">
                                                        {s.maxPowerW != null ? `${Number(s.maxPowerW) / 1000} kW` : "—"}
                                                    </span>

                                                    <span className="text-mute-400"> </span>

                                                    <FontAwesomeIcon icon={faCalendarDays} className="text-mute-500 text-sm" />
                                                    <span>{renderRecurrency(s, t)}</span>

                                                    <span className="text-mute-400"> </span>

                                                    <FontAwesomeIcon icon={faClock} className="text-mute-500 text-sm" />
                                                    <span>
                                                        {formatUtcTimeRange(s, t)}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-4 text-mute-700">
                                                <button
                                                    className="text-primary-600 hover:text-primary-700 disabled:opacity-40 disabled:cursor-not-allowed"
                                                    onClick={() => editSchedule(s)}
                                                    disabled={siteHasMixedOcppVersions}
                                                    title={siteHasMixedOcppVersions ? mixedVersionMessage : t("common.edit")}
                                                >
                                                    <FontAwesomeIcon icon={faPenToSquare} />
                                                </button>

                                                <button
                                                    className="text-red-600 hover:text-red-700"
                                                    onClick={() => {
                                                        setScheduleToRemove(s);
                                                        setConfirmOpen(true);
                                                    }}
                                                    title={t("common.delete")}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        )}
                    </div>
                </div>
            </div>

            <ModalBase
                isOpen={isWizardOpen}
                onClose={() => {
                    setIsWizardOpen(false);
                    setInitialSchedule(null);
                }}
                size="lg"
                className="overflow-y-auto max-h-[90vh]"
            >
                <SiteSmartChargingWizard initialSchedule={initialSchedule} onSubmit={handleWizardSubmit} />
            </ModalBase>

            <ConfirmationModal
                isOpen={confirmOpen}
                title={t("smartCharging.removeSchedule")}
                onClose={() => {
                    setConfirmOpen(false);
                    setScheduleToRemove(null);
                }}
                onConfirm={() => {
                    if (scheduleToRemove?.chargingSitePowerSchedulePublicId) {
                        deleteSchedule(scheduleToRemove.chargingSitePowerSchedulePublicId);
                    }
                    setConfirmOpen(false);
                    setScheduleToRemove(null);
                }}
            />
            <ConfirmationModal
                isOpen={confirmDeleteCapacityOpen}
                title={t("smartCharging.deleteSiteSmartCharging")}
                onClose={() => setConfirmDeleteCapacityOpen(false)}
                onConfirm={deleteCapacity}
            />

        </>
    );
};

export default SiteSmartCharging;

// ================= Helpers =================

function formatDate(value) {
    if (!value) return "—";
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value).split("T")[0];
    return d.toISOString().split("T")[0];
}

function renderRecurrency(s, t) {
    const kind = (s.recurrencyKind ?? "").trim();
    if (!kind) return t("common.allTime");
    if (kind.toLowerCase() === "daily") return t("smartCharging.daily");
    if (kind.toLowerCase() === "weekly") {
        const days = Array.isArray(s.recurrenceDays) ? s.recurrenceDays : [];
        if (!days.length) return t("smartCharging.weeklyNoDays");

        const sortedDays = days.sort((a, b) => {
            if (a === 0) return 1;
            if (b === 0) return -1;
            return a - b;
        });

        return t("smartCharging.weeklyDays", {
            days: sortedDays.map((i) => t(`weekdayShort.${i}`)).join(", "),
        });
    }
    return kind;
}

// DTO -> Wizard initial state
function convertDtoToWizard(dto, capacity) {
    const startDate = formatDate(dto.validFrom);

    return {
        flow: dto?.recurrencyKind ? String(dto.recurrencyKind).toLowerCase() : "daily",

        siteMaxKw: capacity?.maxPowerW != null ? String(Number(capacity.maxPowerW) / 1000) : "",

        existingChargingSitePowerSchedulePublicId: dto.chargingSitePowerSchedulePublicId ?? null,

        startDate: formatDate(dto.validFrom),
        endDate: formatDate(dto.validTo),

        scheduleMaxKw: dto.maxPowerW != null ? String(Number(dto.maxPowerW) / 1000) : "",

        recurrencyKind: dto.recurrencyKind ? dto.recurrencyKind : null,
        recurrenceDays: Array.isArray(dto.recurrenceDays) ? dto.recurrenceDays : [],

        timeOfDayStart: dto.timeOfDayStart
            ? utcTimeToLocalTimeString(dto.timeOfDayStart, startDate)
            : "",

        timeOfDayEnd: dto.timeOfDayEnd
            ? utcTimeToLocalTimeString(dto.timeOfDayEnd, startDate)
            : "",
    };
}

// Wizard -> ChargingSitePowerScheduleDto
function wizardToScheduleDto(w) {
    const kind = w.recurrencyKind ?? null; // "Daily" | "Weekly"

    const validFrom = w.startDate ? `${w.startDate}T00:00:00.000Z` : null;
    const validTo = w.endDate ? `${w.endDate}T23:59:00.000Z` : null;

    const hasWindow = !!(w.timeOfDayStart && w.timeOfDayEnd);
    const referenceDate = w.startDate ?? "1970-01-01";

    return {
        chargingSitePowerSchedulePublicId: w.existingChargingSitePowerSchedulePublicId ?? null,

        validFrom,
        validTo,

        maxPowerW: Number(w.scheduleMaxKw) * 1000,

        recurrencyKind: kind,
        recurrenceDays: kind.toLowerCase() === "weekly" ? (w.recurrenceDays ?? []) : null,

        timeOfDayStart: kind && hasWindow
            ? localTimeToUtcTimeString(w.timeOfDayStart, referenceDate)
            : null,

        timeOfDayEnd: kind && hasWindow
            ? localTimeToUtcTimeString(w.timeOfDayEnd, referenceDate)
            : null,
    };
}
function formatUtcTimeRange(s, t) {
    if (!s.timeOfDayStart || !s.timeOfDayEnd) return t("common.allDay");

    const referenceDate = formatDate(s.validFrom);

    const start = utcTimeToLocalTimeString(s.timeOfDayStart, referenceDate);
    const end = utcTimeToLocalTimeString(s.timeOfDayEnd, referenceDate);

    return `${start} - ${end}`;
}

function mergeSchedules(existing, incoming) {
    const list = Array.isArray(existing) ? [...existing] : [];

    const id = incoming.chargingSitePowerSchedulePublicId;
    if (id) {
        const idx = list.findIndex((x) => x.chargingSitePowerSchedulePublicId === id);
        if (idx >= 0) {
            list[idx] = { ...list[idx], ...incoming };
            return list;
        }
    }

    list.push(incoming);
    return list;
}
